import { createContext, useContext, type FC, type ReactNode, useState } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

interface TabsContextType {
  selectedTab: string
  setSelectedTab: (id: string) => void
}

const TabsContext = createContext<TabsContextType | undefined>(undefined)

const useTabsContext = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs component')
  }
  return context
}

export interface TabsProps extends DefaultProps {
  /**
   * Default selected tab ID
   */
  defaultSelectedTab?: string
  /**
   * Callback when tab selection changes
   */
  onTabChange?: (tabId: string) => void
}

export const Tabs: FC<TabsProps> = ({
  defaultSelectedTab,
  onTabChange,
  className,
  id,
  testId,
  ariaLabel,
  children,
}) => {
  const [selectedTab, setSelectedTab] = useState(defaultSelectedTab || '')

  const handleTabChange = (tabId: string) => {
    setSelectedTab(tabId)
    onTabChange?.(tabId)
  }

  const baseClass = cx('w-full')

  const finalClasses = twMerge(baseClass, className)

  return (
    <TabsContext.Provider value={{ selectedTab, setSelectedTab: handleTabChange }}>
      <div
        className={finalClasses}
        {...(testId ? { 'data-testid': testId } : {})}
        {...(id ? { id } : {})}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      >
        {children}
      </div>
    </TabsContext.Provider>
  )
}

export type TabListProps = DefaultProps

export const TabList: FC<TabListProps> = ({ className, id, testId, ariaLabel, children }) => {
  const baseClass = cx('flex flex-row w-full')

  const finalClasses = twMerge(baseClass, className)

  return (
    <div
      role="tablist"
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {children}
    </div>
  )
}

export interface TabProps extends DefaultProps {
  /**
   * Unique identifier for the tab
   */
  id: string
  /**
   * Tab label
   */
  label: string
  /**
   * Style for the selected tab
   */
  isSelectedStyle?: string
  /**
   * Style for the unselected tab
   */
  isUnselectedStyle?: string
}

export const Tab: FC<TabProps> = ({ id, label, className, testId, ariaLabel, isSelectedStyle, isUnselectedStyle }) => {
  const { selectedTab, setSelectedTab } = useTabsContext()

  const isSelected = selectedTab === id

  const baseClass = cx(
    'px-2xs py-xs cursor-pointer transition-colors duration-200 w-full first-of-type:rounded-tl-lg last-of-type:rounded-tr-lg rounded-b-none',
    {
      'bg-gray-700 text-text-reverse typo-primary-sm-bold': isSelected,
      isSelectedStyle: isSelected,
      'bg-background-light typo-primary-sm-regular border-borders-focus border-borders-dark border-b-gray-700 border':
        !isSelected,
      isUnselectedStyle: !isSelected,
    },
  )

  const finalClasses = twMerge(baseClass, className)

  return (
    <button
      role="tab"
      aria-selected={isSelected}
      aria-controls={`panel-${id}`}
      id={`tab-${id}`}
      tabIndex={isSelected ? 0 : -1}
      onClick={() => {
        setSelectedTab(id)
      }}
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {label}
    </button>
  )
}

export interface TabPanelProps extends DefaultProps {
  /**
   * Unique identifier for the panel
   */
  id: string
}

export const TabPanel: FC<TabPanelProps> = ({ id, className, testId, ariaLabel, children }) => {
  const { selectedTab } = useTabsContext()
  const isSelected = selectedTab === id

  const baseClass = cx({
    hidden: !isSelected,
  })

  const finalClasses = twMerge(baseClass, className)

  return (
    <div
      role="tabpanel"
      id={`panel-${id}`}
      aria-labelledby={`panel-${id}`}
      className={finalClasses}
      hidden={!isSelected}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      {children}
    </div>
  )
}
